import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X, StickyNote, Check, Pencil, Maximize2, Download } from "lucide-react";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import { api } from "@/lib/api";
import PawSpinner from "@/components/PawSpinner";
import PomEmpty from "@/components/PomEmpty";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const CATEGORIES = ["All", "Food", "Movement", "Learn", "Experience", "Other"];
const CATEGORY_COLORS = {
  Food: "bg-orange-100 text-orange-700",
  Movement: "bg-blue-100 text-blue-700",
  Learn: "bg-purple-100 text-purple-700",
  Experience: "bg-emerald-100 text-emerald-700",
  Other: "bg-gray-100 text-gray-600",
};

function CategoryBadge({ category }) {
  return (
    <span className={`text-xs px-2.5 py-0.5 rounded-pill ${CATEGORY_COLORS[category] || CATEGORY_COLORS.Other}`}>
      {category}
    </span>
  );
}

/* ── Upcoming Detail Bottom Sheet ── */
function UpcomingDetail({ item, onClose, onComplete, onDelete, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setTitle(item.title);
    setCategory(item.category);
    setNotes(item.notes || "");
    setEditing(true);
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.updateBucketItem(item.id, { title: title.trim(), category, notes: notes.trim() });
      onUpdated();
      setEditing(false);
      toast.success("Adventure updated!");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div className="flex-1 pr-4">
          {editing ? (
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full font-heading text-2xl text-text-primary bg-transparent border-b border-border-pink pb-1 focus:outline-none focus:border-blush"
              data-testid="edit-upcoming-title" />
          ) : (
            <h2 className="font-heading text-2xl text-text-primary">{item.title}</h2>
          )}
          <div className="mt-2">
            {editing ? (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {CATEGORIES.filter(c => c !== "All").map(c => (
                  <button key={c} onClick={() => setCategory(c)}
                    className={`px-3 py-1 rounded-pill text-xs font-medium transition-colors ${category === c ? "bg-blush text-white" : "bg-cream text-text-secondary border border-border-pink"}`}>
                    {c}
                  </button>
                ))}
              </div>
            ) : (
              <CategoryBadge category={item.category} />
            )}
          </div>
        </div>
        <button onClick={onClose} className="shrink-0 p-1 text-text-secondary hover:text-text-primary" data-testid="close-upcoming-detail">
          <X size={20} />
        </button>
      </div>

      <div className="bg-cream rounded-input p-4">
        <p className="text-xs text-text-secondary font-medium mb-1.5">Notes</p>
        {editing ? (
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder="Places, links, ideas..."
            className="w-full px-0 py-0 bg-transparent text-sm text-text-primary placeholder:text-text-secondary/40 resize-none focus:outline-none"
            data-testid="edit-upcoming-notes" />
        ) : item.notes ? (
          <p className="text-sm text-text-primary whitespace-pre-wrap break-words">{item.notes}</p>
        ) : (
          <p className="text-sm text-text-secondary italic">No notes yet</p>
        )}
      </div>

      {editing ? (
        <div className="flex gap-3">
          <button onClick={() => setEditing(false)} className="flex-1 py-2.5 rounded-pill text-sm font-medium border border-border-pink text-text-secondary hover:bg-cream transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !title.trim()}
            className="flex-1 py-2.5 rounded-pill text-sm font-medium bg-blush hover:bg-blush-dark disabled:opacity-50 text-white transition-colors"
            data-testid="save-upcoming-edit-btn">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      ) : (
        <>
          <button onClick={startEdit}
            className="w-full py-2.5 rounded-pill text-sm font-medium border border-blush text-blush hover:bg-blush/5 transition-colors flex items-center justify-center gap-1.5"
            data-testid="edit-upcoming-btn">
            <Pencil size={14} /> Edit adventure
          </button>
          <button onClick={() => { onClose(); onComplete(item); }}
            className="w-full bg-blush hover:bg-blush-dark text-white py-3 rounded-pill text-sm font-medium transition-colors"
            data-testid="mark-complete-btn">
            Mark as complete
          </button>
          <button onClick={() => { onClose(); onDelete(item); }}
            className="w-full text-blush-dark text-sm font-medium py-2 hover:underline transition-colors"
            data-testid="detail-delete-btn">
            Delete adventure
          </button>
        </>
      )}
    </div>
  );
}

/* ── Past Detail Bottom Sheet ── */
function PastDetail({ item, userId, onClose }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editLiked, setEditLiked] = useState("");
  const [editDisliked, setEditDisliked] = useState("");
  const [editRepeat, setEditRepeat] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  const { data: memory, isLoading } = useQuery({
    queryKey: ["memory-for-item", item?.id],
    queryFn: () => api.getMemoryForItem(userId, item.id),
    enabled: !!item?.id && !!userId,
  });

  const startEdit = () => {
    setEditLiked(memory?.journal_liked || "");
    setEditDisliked(memory?.journal_disliked || "");
    setEditRepeat(memory?.journal_would_repeat || null);
    setEditing(true);
  };

  const handleSave = async () => {
    if (!memory?.id) return;
    setSaving(true);
    try {
      await api.updateMemory(memory.id, {
        journal_liked: editLiked,
        journal_disliked: editDisliked,
        journal_would_repeat: editRepeat,
      });
      queryClient.invalidateQueries({ queryKey: ["memory-for-item", item.id] });
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      setEditing(false);
      toast.success("Feedback updated!");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (url) => {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `waimbow-memory-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Download failed");
    }
  };

  if (!item) return null;

  const photos = [];
  if (memory?.generated_photo_urls?.length) {
    memory.generated_photo_urls.forEach(u => photos.push(u));
  } else if (memory?.generated_photo_url) {
    photos.push(memory.generated_photo_url);
  }
  if (memory?.photo_url) photos.push(memory.photo_url);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-heading text-2xl text-text-primary pr-8">{item.title}</h2>
          <div className="flex items-center gap-2 mt-2">
            <CategoryBadge category={item.category} />
            {memory?.completed_at && (
              <span className="text-sm text-text-secondary">
                {new Date(memory.completed_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
            )}
          </div>
        </div>
        <button onClick={onClose} className="shrink-0 p-1 text-text-secondary hover:text-text-primary" data-testid="close-past-detail">
          <X size={20} />
        </button>
      </div>

      {isLoading ? (
        <PawSpinner />
      ) : (
        <>
          {/* Feedback */}
          <div className="space-y-3">
            <p className="text-xs text-text-secondary font-medium">Feedback</p>
            {editing ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">What she loved</label>
                  <textarea value={editLiked} onChange={e => setEditLiked(e.target.value)} rows={2}
                    className="w-full px-3 py-2.5 rounded-input border border-border-pink bg-white text-text-primary text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blush/30" />
                </div>
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">What she didn't love</label>
                  <textarea value={editDisliked} onChange={e => setEditDisliked(e.target.value)} rows={2}
                    className="w-full px-3 py-2.5 rounded-input border border-border-pink bg-white text-text-primary text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blush/30" />
                </div>
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">Would repeat?</label>
                  <div className="flex gap-2">
                    {["yes", "maybe", "no"].map(v => (
                      <button key={v} onClick={() => setEditRepeat(v)}
                        className={`flex-1 py-2 rounded-pill text-xs font-medium transition-colors capitalize ${editRepeat === v ? "bg-blush text-white" : "bg-cream text-text-secondary border border-border-pink"}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setEditing(false)} className="flex-1 py-2.5 rounded-pill text-sm border border-border-pink text-text-secondary">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-pill text-sm bg-blush text-white font-medium disabled:opacity-50" data-testid="save-feedback-btn">
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-cream rounded-input p-4 space-y-3">
                {memory?.journal_liked ? (
                  <div><p className="text-[11px] text-text-secondary mb-0.5">What she loved</p><p className="text-sm text-text-primary">{memory.journal_liked}</p></div>
                ) : null}
                {memory?.journal_disliked ? (
                  <div><p className="text-[11px] text-text-secondary mb-0.5">What she didn't love</p><p className="text-sm text-text-primary">{memory.journal_disliked}</p></div>
                ) : null}
                {memory?.journal_would_repeat && (
                  <div><p className="text-[11px] text-text-secondary mb-0.5">Would repeat?</p>
                    <span className="text-xs bg-white px-2.5 py-0.5 rounded-pill text-text-primary capitalize border border-border-pink">{memory.journal_would_repeat}</span>
                  </div>
                )}
                {!memory?.journal_liked && !memory?.journal_disliked && !memory?.journal_would_repeat && (
                  <p className="text-sm text-text-secondary italic">No feedback yet</p>
                )}
              </div>
            )}
          </div>

          {/* Images */}
          <div>
            <p className="text-xs text-text-secondary font-medium mb-2">Images</p>
            {photos.length > 0 ? (
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {photos.map((p, i) => (
                  <div key={p} className="relative shrink-0 group">
                    <img src={api.getFileUrl(p)} alt="" className="h-32 w-auto rounded-input object-cover" />
                    <div className="absolute bottom-1.5 right-1.5 flex gap-1">
                      <button onClick={() => setLightboxIdx(i)}
                        className="w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors"
                        data-testid={`zoom-photo-${i}`}>
                        <Maximize2 size={13} className="text-white" />
                      </button>
                      <button onClick={() => handleDownload(api.getFileUrl(p))}
                        className="w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors"
                        data-testid={`download-photo-${i}`}>
                        <Download size={13} className="text-white" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-secondary italic">No memory photos yet</p>
            )}
          </div>

          {/* Lightbox */}
          {lightboxIdx >= 0 && (
            <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
              onClick={() => setLightboxIdx(-1)} data-testid="image-lightbox">
              <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10" onClick={() => setLightboxIdx(-1)}>
                <X size={28} />
              </button>
              {photos.length > 1 && (
                <>
                  <button className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-3xl z-10 px-2"
                    onClick={e => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + photos.length) % photos.length); }}>&lsaquo;</button>
                  <button className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-3xl z-10 px-2"
                    onClick={e => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % photos.length); }}>&rsaquo;</button>
                </>
              )}
              <img src={api.getFileUrl(photos[lightboxIdx])} alt="" className="max-h-[85vh] max-w-[92vw] object-contain rounded-lg"
                onClick={e => e.stopPropagation()} />
              <button
                className="absolute bottom-6 right-6 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                onClick={e => { e.stopPropagation(); handleDownload(api.getFileUrl(photos[lightboxIdx])); }}
                data-testid="lightbox-download">
                <Download size={18} className="text-white" />
              </button>
            </div>
          )}

          {!editing && (
            <button onClick={startEdit}
              className="w-full py-2.5 rounded-pill text-sm font-medium border border-blush text-blush hover:bg-blush/5 transition-colors flex items-center justify-center gap-1.5"
              data-testid="edit-feedback-btn">
              <Pencil size={14} /> Edit feedback
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ── Main Page ── */
export default function BucketList() {
  const { userId } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("upcoming");
  const [activeFilter, setActiveFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newCategory, setNewCategory] = useState("Experience");
  const [newNotes, setNewNotes] = useState("");
  // Edit modal (for upcoming)
  const [editItem, setEditItem] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editNotes, setEditNotes] = useState("");
  // Delete
  const [deleteItem, setDeleteItem] = useState(null);
  // Detail sheets
  const [viewUpcoming, setViewUpcoming] = useState(null);
  const [viewPast, setViewPast] = useState(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["bucket-items", userId, activeFilter],
    queryFn: () => api.listBucketItems(userId, activeFilter === "All" ? null : activeFilter).then(r => r.data),
    enabled: !!userId,
  });

  const { data: allMemories = [] } = useQuery({
    queryKey: ["memories", userId],
    queryFn: () => api.listMemories(userId).then(r => r.data),
    enabled: !!userId && activeTab === "past",
  });

  const invalidateItems = () => {
    queryClient.invalidateQueries({ queryKey: ["bucket-items"] });
    queryClient.invalidateQueries({ queryKey: ["pending-items"] });
  };

  const upcoming = useMemo(() => items.filter(i => i.status === "pending"), [items]);

  const pastItems = useMemo(() => {
    let completed = items.filter(i => i.status === "completed");
    if (dateFrom || dateTo) {
      const memMap = {};
      allMemories.forEach(m => { memMap[m.bucket_item_id] = m; });
      completed = completed.filter(item => {
        const mem = memMap[item.id];
        if (!mem?.completed_at) return false;
        const d = mem.completed_at.slice(0, 10);
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
      });
    }
    return completed;
  }, [items, allMemories, dateFrom, dateTo]);

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    try {
      await api.createBucketItem({ user_id: userId, title: newTitle.trim(), category: newCategory, notes: newNotes.trim() });
      invalidateItems();
      setShowAddModal(false);
      setNewTitle("");
      setNewCategory("Experience");
      setNewNotes("");
      toast.success("Adventure added!");
    } catch { toast.error("Failed to add item"); }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditTitle(item.title);
    setEditCategory(item.category);
    setEditNotes(item.notes || "");
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim() || !editItem) return;
    try {
      await api.updateBucketItem(editItem.id, { title: editTitle.trim(), category: editCategory, notes: editNotes.trim() });
      invalidateItems();
      setEditItem(null);
      toast.success("Adventure updated!");
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      await api.deleteBucketItem(deleteItem.id);
      invalidateItems();
      setDeleteItem(null);
      toast.success("Adventure removed");
    } catch { toast.error("Failed to delete"); }
  };

  if (isLoading) return <div className="flex justify-center py-20"><PawSpinner /></div>;

  return (
    <div className="space-y-5 py-2 pb-8" data-testid="bucket-list-screen">
      {/* Header */}
      <h1 className="font-heading text-2xl text-text-primary">Mama's Adventures</h1>

      {/* Tabs */}
      <div className="flex border-b border-border-pink" data-testid="adventure-tabs">
        {["upcoming", "past"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 pb-3 text-sm font-medium text-center capitalize transition-colors ${
              activeTab === tab
                ? "text-text-primary border-b-2 border-blush"
                : "text-text-secondary"
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab === "upcoming" ? "Upcoming" : "Past"}
          </button>
        ))}
      </div>

      {/* Date filter — Past tab only */}
      {activeTab === "past" && (
        <div className="flex gap-3" data-testid="date-filter">
          <div className="flex-1">
            <label className="text-xs text-text-secondary mb-1 block">From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="w-full px-3 py-2.5 rounded-input border border-border-pink bg-white text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-blush/30"
              data-testid="date-from" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-text-secondary mb-1 block">To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="w-full px-3 py-2.5 rounded-input border border-border-pink bg-white text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-blush/30"
              data-testid="date-to" />
          </div>
        </div>
      )}

      {/* Category filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="category-filters">
        {CATEGORIES.map(cat => (
          <button key={cat} onClick={() => setActiveFilter(cat)}
            className={`px-4 py-1.5 rounded-pill text-sm font-medium whitespace-nowrap transition-colors ${
              activeFilter === cat ? "bg-blush text-white" : "bg-white text-text-secondary border border-border-pink hover:bg-cream"
            }`}
            data-testid={`filter-${cat.toLowerCase()}`}>
            {cat}
          </button>
        ))}
      </div>

      {/* Item Lists */}
      {activeTab === "upcoming" ? (
        upcoming.length > 0 ? (
          <div className="space-y-3">
            {upcoming.map(item => (
              <div key={item.id} onClick={() => setViewUpcoming(item)}
                className="bg-white rounded-card shadow-soft p-5 border-l-4 border-blush cursor-pointer card-hover"
                data-testid={`bucket-item-${item.id}`}>
                <p className="text-text-primary font-medium">{item.title}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <CategoryBadge category={item.category} />
                  <span className="text-xs text-text-secondary">{new Date(item.created_at).toLocaleDateString()}</span>
                  {item.notes && <StickyNote size={12} className="text-gold" />}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <PomEmpty message="No upcoming adventures. Add one!" />
        )
      ) : (
        pastItems.length > 0 ? (
          <div className="space-y-3">
            {pastItems.map(item => (
              <div key={item.id} onClick={() => setViewPast(item)}
                className="bg-white rounded-card shadow-soft p-5 cursor-pointer card-hover"
                data-testid={`bucket-item-${item.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-text-primary font-medium">{item.title}</p>
                  <div className="w-7 h-7 rounded-full bg-sage/20 flex items-center justify-center shrink-0">
                    <Check size={14} className="text-sage" />
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <CategoryBadge category={item.category} />
                  <span className="text-xs text-text-secondary">{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <PomEmpty message={dateFrom || dateTo ? "No adventures in this period" : "No past adventures yet"} />
        )
      )}

      {/* FAB — Add new */}
      <button
        onClick={() => setShowAddModal(true)}
        className="fixed bottom-[88px] right-5 md:bottom-8 md:right-8 w-14 h-14 rounded-full bg-blush hover:bg-blush-dark text-white flex items-center justify-center z-30 transition-colors"
        style={{ boxShadow: "0 4px 16px rgba(232,160,180,0.4)" }}
        data-testid="add-adventure-fab"
      >
        <Plus size={24} />
      </button>

      {/* Upcoming Detail Sheet */}
      <Dialog open={!!viewUpcoming} onOpenChange={() => setViewUpcoming(null)}>
        <DialogContent className="rounded-t-card sm:rounded-card bg-white border-border-pink max-w-md mx-0 sm:mx-4 fixed bottom-0 sm:relative sm:bottom-auto max-h-[80vh] overflow-y-auto p-6">
          <UpcomingDetail
            item={viewUpcoming}
            onClose={() => setViewUpcoming(null)}
            onComplete={(item) => navigate(`/complete/${item.id}`)}
            onDelete={(item) => setDeleteItem(item)}
            onUpdated={() => { invalidateItems(); setViewUpcoming(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Past Detail Sheet */}
      <Dialog open={!!viewPast} onOpenChange={() => setViewPast(null)}>
        <DialogContent className="rounded-t-card sm:rounded-card bg-white border-border-pink max-w-md mx-0 sm:mx-4 fixed bottom-0 sm:relative sm:bottom-auto max-h-[80vh] overflow-y-auto p-6">
          <PastDetail item={viewPast} userId={userId} onClose={() => setViewPast(null)} />
        </DialogContent>
      </Dialog>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="rounded-card bg-white border-border-pink max-w-md mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-text-primary">New Adventure</DialogTitle>
            <DialogDescription className="text-text-secondary text-sm">What would you like to try?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Try pottery class"
              className="w-full px-4 py-3 rounded-input border border-border-pink bg-white text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm"
              data-testid="new-item-title-input" autoFocus />
            <div>
              <label className="text-sm text-text-secondary mb-2 block">Category</label>
              <div className="flex flex-wrap gap-2" data-testid="category-selector">
                {CATEGORIES.filter(c => c !== "All").map(cat => (
                  <button key={cat} onClick={() => setNewCategory(cat)}
                    className={`px-4 py-1.5 rounded-pill text-sm font-medium transition-colors ${newCategory === cat ? "bg-blush text-white" : "bg-cream text-text-secondary border border-border-pink"}`}
                    data-testid={`category-option-${cat.toLowerCase()}`}>{cat}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm text-text-secondary mb-1.5 block">Notes & plans <span className="text-text-secondary/50">(optional)</span></label>
              <textarea value={newNotes} onChange={e => setNewNotes(e.target.value)} rows={4}
                placeholder={"Places to check out, links, ideas...\ne.g. https://instagram.com/p/...\nThat café on 5th Ave that looks cozy"}
                className="w-full px-4 py-3 rounded-input border border-border-pink bg-white text-text-primary placeholder:text-text-secondary/40 focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm resize-none"
                data-testid="new-item-notes-input" />
            </div>
            <button onClick={handleAdd} disabled={!newTitle.trim()}
              className="w-full bg-blush hover:bg-blush-dark disabled:opacity-50 text-white py-3 rounded-pill text-sm font-medium transition-colors"
              data-testid="save-adventure-btn">Save Adventure</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent className="rounded-card bg-white border-border-pink max-w-md mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-text-primary">Edit Adventure</DialogTitle>
            <DialogDescription className="text-text-secondary text-sm">Update adventure details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)}
              className="w-full px-4 py-3 rounded-input border border-border-pink bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm"
              data-testid="edit-item-title-input" autoFocus />
            <div>
              <label className="text-sm text-text-secondary mb-2 block">Category</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.filter(c => c !== "All").map(cat => (
                  <button key={cat} onClick={() => setEditCategory(cat)}
                    className={`px-4 py-1.5 rounded-pill text-sm font-medium transition-colors ${editCategory === cat ? "bg-blush text-white" : "bg-cream text-text-secondary border border-border-pink"}`}>{cat}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm text-text-secondary mb-1.5 block">Notes & plans</label>
              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={4} placeholder="Places, links, ideas..."
                className="w-full px-4 py-3 rounded-input border border-border-pink bg-white text-text-primary placeholder:text-text-secondary/40 focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm resize-none"
                data-testid="edit-item-notes-input" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditItem(null)} className="flex-1 py-3 rounded-pill text-sm font-medium border border-border-pink text-text-secondary hover:bg-cream transition-colors">Cancel</button>
              <button onClick={handleSaveEdit} disabled={!editTitle.trim()} className="flex-1 bg-blush hover:bg-blush-dark disabled:opacity-50 text-white py-3 rounded-pill text-sm font-medium transition-colors" data-testid="save-edit-btn">Save changes</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <DialogContent className="rounded-card bg-white border-border-pink max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-text-primary">Remove adventure?</DialogTitle>
            <DialogDescription className="text-text-secondary text-sm">"{deleteItem?.title}" will be permanently removed.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setDeleteItem(null)} className="flex-1 py-3 rounded-pill text-sm font-medium border border-border-pink text-text-secondary hover:bg-cream transition-colors" data-testid="cancel-delete-btn">Keep it</button>
            <button onClick={handleDelete} className="flex-1 bg-blush-dark hover:bg-blush-dark/90 text-white py-3 rounded-pill text-sm font-medium transition-colors" data-testid="confirm-delete-btn">Remove</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
